import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../services/api";

export default function WeightPage() {
  const navigate = useNavigate();
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Expanded/Collapsed state for History Card (Default: expanded = true)
  const [historyExpanded, setHistoryExpanded] = useState(true);

  // Form & Editing states for Weight Entries
  const [editingId, setEditingId] = useState(null);
  const [weightKg, setWeightKg] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");

  // Inline Goal Editing states
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [editGoalWeight, setEditGoalWeight] = useState("");
  const [editGoalDate, setEditGoalDate] = useState("");
  const [editWeightFreq, setEditWeightFreq] = useState(7);
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalFeedback, setGoalFeedback] = useState("");

  const fetchData = async () => {
    try {
      const [lRes, hRes, pRes] = await Promise.all([
        api.get("/readings/weight/latest").catch(() => null),
        api.get("/readings/weight/history").catch(() => null),
        api.get("/users/profile").catch(() => null),
      ]);

      if (lRes?.data?.latest) setLatest(lRes.data.latest);
      if (hRes?.data?.items) setHistory(hRes.data.items);
      if (pRes?.data?.profile) {
        setProfile(pRes.data.profile);
      }
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStartEditGoal = () => {
    setEditGoalWeight(profile?.goalWeight ? String(profile.goalWeight).replace(".", ",") : "");
    setEditGoalDate(profile?.goalDate ? String(profile.goalDate).split("T")[0] : "");
    setEditWeightFreq(profile?.weightFrequencyDays ? Number(profile.weightFrequencyDays) : 7);
    setGoalFeedback("");
    setIsEditingGoal(true);
  };

  const handleSaveGoal = async (e) => {
    e.preventDefault();
    if (!editGoalWeight) return;
    setSavingGoal(true);
    setGoalFeedback("");

    try {
      const parsedGoalWeight = Number(String(editGoalWeight).replace(",", "."));
      const updatedProfilePayload = {
        ...profile,
        goalWeight: parsedGoalWeight,
        goalDate: editGoalDate || null,
        weightFrequencyDays: Number(editWeightFreq) || 7,
      };

      await api.put("/users/profile", updatedProfilePayload);
      setGoalFeedback("Meta atualizada com sucesso! 🎯");
      setIsEditingGoal(false);
      await fetchData();
    } catch {
      setGoalFeedback("Erro ao atualizar a meta. Tente novamente.");
    } finally {
      setSavingGoal(false);
    }
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setWeightKg(String(item.weightKg).replace(".", ","));
    const dStr = item.loggedAt ? String(item.loggedAt).split("T")[0] : new Date().toISOString().split("T")[0];
    setDate(dStr);
    setNotes(item.notes || "");
    setFeedback("");
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setWeightKg("");
    setDate(new Date().toISOString().split("T")[0]);
    setNotes("");
    setFeedback("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!weightKg) return;
    setSaving(true);
    setFeedback("");

    const payload = {
      weightKg: Number(String(weightKg).replace(",", ".")),
      date,
      notes: notes.slice(0, 50),
    };

    try {
      if (editingId) {
        await api.put(`/readings/weight/${editingId}`, payload);
        setFeedback("Registro de peso atualizado com sucesso! ⚖️");
      } else {
        await api.post("/readings/weight", payload);
        setFeedback("Peso registrado com sucesso! ⚖️");
      }
      handleCancelEdit();
      await fetchData();
    } catch {
      setFeedback("Erro ao salvar peso. Verifique os dados.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este registro de peso?")) return;
    try {
      await api.delete(`/readings/weight/${id}`);
      await fetchData();
    } catch {
      alert("Não foi possível excluir o registro.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 py-4 animate-pulse">
        <div className="h-32 rounded-3xl skeleton-loading w-full" />
        <div className="h-44 rounded-3xl skeleton-loading w-full" />
        <div className="h-44 rounded-3xl skeleton-loading w-full" />
        <div className="h-44 rounded-3xl skeleton-loading w-full" />
      </div>
    );
  }

  const currentWeight = latest?.weightKg ? Number(latest.weightKg) : (profile?.startWeight ? Number(profile.startWeight) : null);
  const goalWeight = profile?.goalWeight ? Number(profile.goalWeight) : null;
  const heightCm = profile?.height ? Number(profile.height) : null;
  const startWeight = profile?.startWeight ? Number(profile.startWeight) : null;

  const calculateBMI = (w, h) => {
    if (!w || !h || h <= 0) return null;
    const hM = h / 100;
    return (w / (hM * hM)).toFixed(1);
  };

  const currentBmi = calculateBMI(currentWeight, heightCm);
  const goalBmi = calculateBMI(goalWeight, heightCm);

  // Cálculo de mensagens e status da meta
  let progressPercent = 50;
  let statusText = "Em acompanhamento";
  let statusGoalText = "OBJETIVO: MANTER PESO";
  let statusColorClass = "text-emerald-800 bg-emerald-50 border-emerald-200";

  if (currentWeight && goalWeight) {
    const diff = currentWeight - goalWeight;
    const absDiff = Math.abs(diff);
    const formattedAbsDiff = absDiff.toFixed(1).replace(".", ",");

    if (absDiff <= 0.3) {
      // CASO A: Na meta (diferença até 0,3 kg) -> VERDE
      progressPercent = 100;
      statusText = "Você está exatamente na meta!";
      statusGoalText = "OBJETIVO: MANTER PESO";
      statusColorClass = "text-emerald-800 bg-emerald-50 border-emerald-200";
    } else if (absDiff <= 2.0) {
      // CASO B: Até 2 kg da meta -> VERDE
      statusText = "Você está muito próximo da meta!";
      if (diff > 0) {
        const range = (startWeight && startWeight > goalWeight) ? (startWeight - goalWeight) : 10;
        const done = range - diff;
        progressPercent = Math.max(15, Math.min(95, Math.round((done / range) * 100)));
        statusGoalText = `OBJETIVO: REDUZIR ${formattedAbsDiff} KG`;
      } else {
        progressPercent = 95;
        statusGoalText = `OBJETIVO: AUMENTAR ${formattedAbsDiff} KG`;
      }
      statusColorClass = "text-emerald-800 bg-emerald-50 border-emerald-200";
    } else {
      // CASO C: Mais de 2 kg da meta -> LARANJA
      if (diff > 0) {
        const range = (startWeight && startWeight > goalWeight) ? (startWeight - goalWeight) : 10;
        const done = range - diff;
        progressPercent = Math.max(10, Math.min(90, Math.round((done / range) * 100)));
        statusText = `${formattedAbsDiff} kg acima da meta`;
        statusGoalText = `OBJETIVO: REDUZIR ${formattedAbsDiff} KG`;
      } else {
        progressPercent = 60;
        statusText = `${formattedAbsDiff} kg abaixo da meta`;
        statusGoalText = `OBJETIVO: AUMENTAR ${formattedAbsDiff} KG`;
      }
      statusColorClass = "text-amber-800 bg-amber-50 border-amber-200";
    }
  }

  const weightFreq = profile?.weightFrequencyDays ? Number(profile.weightFrequencyDays) : 7;
  const freqLabel = weightFreq === 1 ? "1 dia (Diário)" : `${weightFreq} dias`;

  return (
    <div className="space-y-4 pb-24">
      {/* BOTÃO VOLTAR PARA A HOME */}
      <div className="flex items-center justify-between pb-1">
        <Link
          to="/app/home"
          className="inline-flex items-center gap-1.5 text-xs font-extrabold text-[#6C54D8] hover:text-[#5A45BF] bg-purple-100/80 hover:bg-purple-200/80 px-3.5 py-2 rounded-2xl border border-purple-200/60 transition-all cursor-pointer shadow-2xs"
        >
          <span className="text-sm">←</span>
          <span>Voltar</span>
        </Link>
      </div>

      {/* CARD 1 — PESO ATUAL */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-2.5">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#6D6680] block">
              Peso Atual
            </span>
            <h2 className="text-3xl font-black text-[#24152F] font-serif mt-0.5">
              {currentWeight ? `${currentWeight.toString().replace(".", ",")} kg` : "—"}
            </h2>
          </div>

          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-purple-100/80 text-[#6C54D8] border border-purple-200/50 text-2xl">
            ⚖️
          </div>
        </div>

        <div className="space-y-1 pt-1 border-t border-purple-100/60">
          <p className="text-xs font-semibold text-purple-900">
            {latest?.loggedAt ? (
              <>Data do registro · {new Date(latest.loggedAt).toLocaleDateString("pt-BR")}</>
            ) : (
              "Sem registros recentes"
            )}
          </p>
          {currentBmi && (
            <p className="text-xs font-medium text-gray-600">
              IMC estimado: <strong className="font-bold text-[#24152F]">{currentBmi.replace(".", ",")} kg/m²</strong>
            </p>
          )}
        </div>
      </div>

      {/* CARD 2 — REGISTRAR PESO (POSICIONADO ACIMA DA META CONFORME SOLICITADO) */}
      <form onSubmit={handleSave} className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3.5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#24152F]">
            {editingId ? "✏️ Editar Peso" : "Registrar Peso"}
          </h3>
          {editingId && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="text-xs font-bold text-gray-500 hover:text-gray-700 cursor-pointer underline"
            >
              Cancelar
            </button>
          )}
        </div>

        {feedback && (
          <div className="p-3 rounded-2xl bg-purple-50 border border-purple-200 text-xs font-bold text-purple-800">
            {feedback}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
              Peso (kg)
            </label>
            <input
              type="text"
              placeholder="Ex: 58,5"
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
              className="input-base h-11 w-full text-sm font-bold box-border"
              required
            />
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
              Data
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input-base h-11 w-full text-sm font-bold box-border"
              required
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">
              Observação (máx. 50 caracteres)
            </label>
            <span className="text-[10px] text-gray-400 font-semibold">{notes.length}/50</span>
          </div>
          <input
            type="text"
            maxLength={50}
            placeholder="Ex: Estava menstruada e com retenção"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input-base h-11 w-full text-sm box-border"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full h-12 rounded-2xl bg-[#6C54D8] hover:bg-[#5A45BF] active:scale-95 text-white text-xs font-black uppercase tracking-wider shadow-md transition-all cursor-pointer disabled:opacity-60"
        >
          {saving ? "Salvando..." : editingId ? "Atualizar Peso" : "Salvar Peso"}
        </button>
      </form>

      {/* CARD 3 — META (COM EDIÇÃO INLINE E SELEÇÃO DE PERIODICIDADE) */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">🎯</span>
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-[#24152F]">
              Meta
            </h3>
          </div>

          {!isEditingGoal && (
            <button
              type="button"
              onClick={handleStartEditGoal}
              className="text-xs font-bold text-[#6C54D8] hover:text-[#5A45BF] cursor-pointer px-3 py-1 rounded-xl bg-purple-50 hover:bg-purple-100 transition-all border border-purple-200/60"
            >
              Editar meta
            </button>
          )}
        </div>

        {goalFeedback && (
          <div className="p-2.5 rounded-xl bg-purple-50 border border-purple-200 text-xs font-bold text-purple-800">
            {goalFeedback}
          </div>
        )}

        {/* MODO EDIÇÃO INLINE DA META */}
        {isEditingGoal ? (
          <form onSubmit={handleSaveGoal} className="p-4 rounded-2xl bg-purple-50/80 border border-purple-200 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-extrabold uppercase tracking-wider text-[#24152F]">
                ✏️ Editar Meta de Peso
              </h4>
              <button
                type="button"
                onClick={() => setIsEditingGoal(false)}
                className="text-xs font-bold text-gray-500 hover:text-gray-700 underline cursor-pointer"
              >
                Cancelar
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                  Peso Meta (kg)
                </label>
                <input
                  type="text"
                  placeholder="Ex: 60,0"
                  value={editGoalWeight}
                  onChange={(e) => setEditGoalWeight(e.target.value)}
                  className="input-base h-11 w-full text-sm font-bold box-border"
                  required
                />
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                  Data planejada para atingir a meta
                </label>
                <input
                  type="date"
                  value={editGoalDate}
                  onChange={(e) => setEditGoalDate(e.target.value)}
                  className="input-base h-11 w-full text-sm font-bold box-border"
                />
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                  Registrar peso a cada
                </label>
                <select
                  value={editWeightFreq}
                  onChange={(e) => setEditWeightFreq(Number(e.target.value))}
                  className="input-base h-11 w-full text-sm font-bold box-border bg-white cursor-pointer"
                >
                  <option value={1}>1 dia (Diário)</option>
                  <option value={3}>3 dias</option>
                  <option value={7}>7 dias (1 semana)</option>
                  <option value={14}>14 dias (2 semanas)</option>
                  <option value={30}>30 dias (1 mês)</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="submit"
                disabled={savingGoal}
                className="h-11 px-5 rounded-2xl bg-[#6C54D8] hover:bg-[#5A45BF] text-white text-xs font-black uppercase tracking-wider shadow-md transition-all cursor-pointer disabled:opacity-60"
              >
                {savingGoal ? "Salvando..." : "Salvar Meta"}
              </button>
              <button
                type="button"
                onClick={() => setIsEditingGoal(false)}
                className="h-11 px-4 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold transition-all cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </form>
        ) : (
          /* EXIBIÇÃO DA META */
          goalWeight ? (
            <div className="space-y-2.5">
              <div>
                <p className="text-sm text-[#24152F]">
                  Pesar <strong className="font-extrabold">{goalWeight.toString().replace(".", ",")} kg</strong>{" "}
                  {profile?.goalDate ? `em ${new Date(profile.goalDate).toLocaleDateString("pt-BR")}` : ""}
                </p>
                {goalBmi && (
                  <p className="text-xs font-medium text-gray-600 mt-0.5">
                    IMC estimado na meta: <strong className="font-bold text-[#24152F]">{goalBmi.replace(".", ",")} kg/m²</strong>
                  </p>
                )}
                <p className="text-xs font-medium text-purple-800 mt-1">
                  Acompanhamento: <strong className="font-bold text-[#24152F]">A cada {freqLabel}</strong>
                </p>
              </div>

              {/* CAIXA DE MENSAGEM PADRONIZADA EM 2 LINHAS */}
              <div className={`p-3.5 rounded-2xl border flex flex-col gap-1 ${statusColorClass}`}>
                <p className="text-xs font-extrabold">{statusText}</p>
                <p className="text-[10px] font-black uppercase tracking-wider opacity-90">{statusGoalText}</p>
              </div>
            </div>
          ) : (
            <div className="p-3 rounded-2xl bg-purple-50/50 border border-purple-100 text-xs font-semibold text-purple-800 flex items-center justify-between">
              <span>Nenhuma meta cadastrada. Defina seu objetivo de peso.</span>
              <button
                type="button"
                onClick={handleStartEditGoal}
                className="text-xs font-extrabold text-[#6C54D8] underline cursor-pointer shrink-0 ml-2"
              >
                Cadastrar meta
              </button>
            </div>
          )
        )}
      </div>

      {/* CARD 4 — HISTÓRICO DE PESAGENS */}
      <div className="glass-card rounded-3xl p-5 border border-purple-100/70 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-extrabold uppercase tracking-wider text-[#6D6680]">
            Histórico de Pesagens
          </h3>
          <button
            type="button"
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="text-xs font-bold text-[#6C54D8] hover:text-[#5A45BF] cursor-pointer flex items-center gap-1"
          >
            {historyExpanded ? "Ocultar histórico" : `Exibir Histórico (${history.length})`}
          </button>
        </div>

        {historyExpanded && (
          <div className="space-y-3 pt-1">
            {history.length > 0 ? (
              history.map((item) => (
                <div
                  key={item.id}
                  className="p-3.5 rounded-2xl border border-purple-100/80 bg-white/90 space-y-1.5 transition-all hover:border-purple-200"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-semibold text-gray-500">
                        {item.loggedAt ? new Date(item.loggedAt).toLocaleDateString("pt-BR") : "—"}
                      </span>
                      <span className="text-base font-black text-[#24152F]">
                        {item.weightKg.toString().replace(".", ",")} kg
                      </span>
                      {item.diffKg !== null && item.diffKg !== undefined && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                            item.diffKg < 0
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : item.diffKg > 0
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-gray-100 text-gray-700 border border-gray-200"
                          }`}
                        >
                          {item.diffKg > 0 ? `+${item.diffKg.toFixed(1).replace(".", ",")} kg` : `${item.diffKg.toFixed(1).replace(".", ",")} kg`}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs font-bold">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(item)}
                        className="text-[#6C54D8] hover:text-[#5A45BF] cursor-pointer"
                      >
                        Editar
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500 hover:text-red-700 cursor-pointer"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>

                  {item.notes && (
                    <p className="text-xs text-gray-600 font-medium line-clamp-2 pt-0.5 border-t border-gray-100 mt-1">
                      {item.notes}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 font-medium py-2">
                Nenhum registro no histórico.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
